/**
 * Pure text-search helpers (no pdf.js / DOM here, so they unit-test cleanly).
 * Text extraction lives in pdf-text.ts; this module just finds query matches
 * across already-extracted per-page text and builds display snippets.
 */

/** A rectangle in PDF user space (origin bottom-left, points). */
export interface PdfRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** One pdf.js text run, with its offset range in the page text + its PDF-space box. */
export interface TextItemGeom {
  readonly start: number; // char offset of this run within the page text
  readonly length: number; // run length (str.length)
  readonly rect: PdfRect;
}

export interface PageText {
  readonly pageIndex: number; // 0-based
  readonly text: string;
  /** Per-run geometry, for mapping a match back to on-page rectangles (optional). */
  readonly items?: readonly TextItemGeom[];
}

export interface SearchMatch {
  readonly pageIndex: number; // 0-based page the match is on
  readonly start: number; // char offset of the match within that page's text
  readonly length: number; // length of the matched text
  readonly snippet: string; // context window around the match (for the results list)
  readonly matchInSnippet: readonly [number, number]; // [start, end) of the match within `snippet`
}

const SNIPPET_RADIUS = 32;
const ELLIPSIS = '…';

/**
 * Build a context snippet around a match and report where the match sits inside
 * it, so the UI can emphasize the matched run. Surrounding whitespace is
 * collapsed for a tidy single-line display.
 */
export function makeSnippet(
  text: string,
  start: number,
  length: number,
): { snippet: string; matchInSnippet: [number, number] } {
  const from = Math.max(0, start - SNIPPET_RADIUS);
  const to = Math.min(text.length, start + length + SNIPPET_RADIUS);

  const rawBefore = text.slice(from, start).replace(/\s+/g, ' ');
  const rawMatch = text.slice(start, start + length).replace(/\s+/g, ' ');
  const rawAfter = text.slice(start + length, to).replace(/\s+/g, ' ');

  const prefix = from > 0 ? ELLIPSIS : '';
  const suffix = to < text.length ? ELLIPSIS : '';

  const matchStart = prefix.length + rawBefore.length;
  const snippet = `${prefix}${rawBefore}${rawMatch}${rawAfter}${suffix}`;
  return { snippet, matchInSnippet: [matchStart, matchStart + rawMatch.length] };
}

/**
 * Find every case-insensitive occurrence of `query` across the pages, in page
 * order. `maxMatches` bounds the result set so a pathological query (e.g. a
 * single common letter) can't lock up the UI; when hit, the caller should tell
 * the user the list was truncated.
 */
export function findMatches(
  pages: readonly PageText[],
  query: string,
  maxMatches = 500,
): SearchMatch[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [];

  const matches: SearchMatch[] = [];
  for (const page of pages) {
    const haystack = page.text.toLowerCase();
    let from = 0;
    for (;;) {
      const idx = haystack.indexOf(q, from);
      if (idx < 0) break;
      const { snippet, matchInSnippet } = makeSnippet(page.text, idx, q.length);
      matches.push({ pageIndex: page.pageIndex, start: idx, length: q.length, snippet, matchInSnippet });
      if (matches.length >= maxMatches) return matches;
      from = idx + q.length;
    }
  }
  return matches;
}

/**
 * Map a match (offset range within a page's text) to the PDF-space rectangles of
 * the text runs it overlaps. Run-level granularity: a substring of a longer run
 * highlights the whole run, which is fine for a find-on-page indicator.
 */
export function matchRects(
  items: readonly TextItemGeom[] | undefined,
  start: number,
  length: number,
): PdfRect[] {
  if (!items || items.length === 0) return [];
  const end = start + length;
  const rects: PdfRect[] = [];
  for (const it of items) {
    const itEnd = it.start + it.length;
    if (it.start < end && itEnd > start) rects.push(it.rect); // ranges overlap
  }
  return rects;
}
